'use client';

import {
  useState, useRef, useEffect, useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import { Anchor, Button } from '@components/common';
import { FloatingNavBar } from '@components/features/job-list-content/FloatingNavBar';
import { FloatingNavBarOverflowMenu } from '@components/features/job-list-content/FloatingNavBarOverflowMenu';
import { BulkOperationProps } from '@type/bulk-operations';
import { useApplicantFilterDrawer } from '@components/contexts/ApplicantFilterDrawerContext';
import { useCompanyApplicant } from '@components/contexts/CompanyApplicantContext';
import { getI18nLinkHref } from '@services/i18nPaths';
import { BulkActionsPanel } from './BulkActionsPanel';

/**
 * @description Company-specific floating nav bar for the applicant listing inside Mission Control.
 *
 * Buttons (left → right):
 * - **Upload CVs** — placeholder; functionality to be developed.
 * - **Filter** — opens/closes the applicant filter drawer via ApplicantFilterDrawerContext.
 * - **Flow/Rank toggle** — switches between column (flow) and list (rank) views.
 * - **Actions** — reveals BulkActionsPanel chips above the pill.
 */
export const CompanyApplicantFloatingNavBar = (props: BulkOperationProps) => {
  const { locale, jobSlug, ...bulkProps } = props;
  const { t } = useTranslation('common');

  const filterDrawer = useApplicantFilterDrawer();
  const { currentView, setCurrentView } = useCompanyApplicant();

  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [barWidth, setBarWidth] = useState<number>();
  const barRef = useRef<HTMLDivElement>(null);
  const chipAreaRef = useRef<HTMLDivElement>(null);

  // Keep bar width in sync for the actions panel
  useEffect(() => {
    const barElement = barRef.current;
    if (!barElement) return undefined;

    const updateBarWidth = () => setBarWidth(barElement.getBoundingClientRect().width);
    updateBarWidth();

    const resizeObserver = new ResizeObserver(updateBarWidth);
    resizeObserver.observe(barElement);
    return () => resizeObserver.disconnect();
  }, []);

  // Close actions panel on outside click
  useEffect(() => {
    if (!isActionsOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inChipArea = chipAreaRef.current?.contains(target);
      const inBar = barRef.current?.contains(target);
      const inDialog = target instanceof HTMLElement && target.closest('[role="dialog"]') !== null;
      if (!inChipArea && !inBar && !inDialog) setIsActionsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isActionsOpen]);

  const handleToggleActions = useCallback(() => {
    setIsActionsOpen((prev) => !prev);
  }, []);

  return (
    <FloatingNavBar
      barRef={barRef}
      pillBackground="surface-100"
      inline
      above={isActionsOpen ? (
        <div ref={chipAreaRef} className="self-end">
          <BulkActionsPanel
            locale={locale}
            jobSlug={jobSlug}
            barWidth={barWidth}
            {...bulkProps}
          />
        </div>
      ) : null}
    >
      <FloatingNavBarOverflowMenu disableOverflow>
        {/* Filter */}
        <Button
          type="button"
          styling="solid"
          color="light"
          className="!h-8 whitespace-nowrap !rounded-2xl !px-2.5 text-xs lg:!h-11 lg:!px-8 lg:text-lg"
          onClick={filterDrawer?.toggleDrawer}
        >
          {t('filters.filters-nav.filter-button-label')}
        </Button>

        {/* Flow / Rank toggle */}
        {currentView === 'rank' ? (
          <Anchor
            href={getI18nLinkHref(`/mc/job/${jobSlug}/applicants/flow`, locale)}
            styling="solid"
            color="light"
            className="!h-8 whitespace-nowrap !rounded-2xl !px-2.5 text-xs lg:!h-11 lg:!px-8 lg:text-lg"
          >
            {t('pipeline.view.button.column.view.title')}
          </Anchor>
        ) : (
          <Button
            type="button"
            styling="solid"
            color="light"
            className="!h-8 whitespace-nowrap !rounded-2xl !px-2.5 text-xs lg:!h-11 lg:!px-8 lg:text-lg"
            onClick={() => setCurrentView('rank')}
          >
            {t('pipeline.view.button.list.view.title')}
          </Button>
        )}

        {/* Actions (bulk operations) */}
        <Button
          type="button"
          styling="solid"
          color="light"
          className="!h-8 whitespace-nowrap !rounded-2xl !px-2.5 text-xs lg:!h-11 lg:!px-8 lg:text-lg"
          onClick={handleToggleActions}
        >
          {t('global.label.actions')}
        </Button>
      </FloatingNavBarOverflowMenu>
    </FloatingNavBar>
  );
};
